import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AppState } from '../../store/state';
import { AppDispatch } from '../../store/store';
import { Submission } from '../../store/submission/types';
import {
  selectBidState,
  selectInfo,
  selectKeywordState,
  selectSubmissionState,
  selectTrackRoles,
} from '../../store/selectors';
import { connect } from 'react-redux';
import Loading from '../../components/Loading/Loading';
import { ChairInfo, Info, InfoDeleteDTO, TableFriendlyName } from '../../store/info/types';
import { getSubmissionRow } from '../../helpers/table';
import MyGridRedux from '../../components/MyGridRedux/MyGridRedux';
import { useTranslation } from 'react-i18next';
import TableHeader from '../../components/TableHeader/TableHeader';
import { Role } from '../../store/conference/types';
import infoSlice from '../../store/info/slice';
import { getRouteByName } from '../../router/routes';
import history from '../../store/history';
import { fillRoutePath } from '../../helpers/path';
import { BidOption, ReviewerTableRow } from '../../store/bid/types';
import { getReviewer } from '../../helpers/users';
import tableSlice from '../../store/table/slice';
import { buildDTO } from '../../store/review/utils';
import { Assignment, UpdateAssignment } from '../../store/review/types';
import assignmentSlice from '../../store/review/slice';
import useFetchTableData from '../../store/table/useFetchTableData';

interface Props {
  submissionsById: { [key: string]: Submission };
  loading: boolean;
  keywordById: { [key: number]: Keyword };
  info: Info;
  roleById: { [key: string]: Role };
  bidOptionsById: { [key: number]: BidOption };
  clearNotifiablesAction: () => void;
  updateAssignmentAction: (data: UpdateAssignment) => void;
  deleteInfoAction: (data: InfoDeleteDTO) => void;
}

const ReviewersToPapersDetailPage = ({
  submissionsById,
  loading,
  keywordById,
  info,
  roleById,
  bidOptionsById,
  clearNotifiablesAction,
  updateAssignmentAction,
  deleteInfoAction,
}: Props) => {
  const { t, i18n } = useTranslation();
  const params: any = useParams();
  const id = parseInt(params.id);

  const friendlyName: TableFriendlyName = 'reviewer_assignments';
  const queryParams = { person_role_id: id };

  const [fetchData, fetchCustomColumnsData, getInfoAction] = useFetchTableData(friendlyName, queryParams);

  useEffect(() => {
    if (Object.keys(info['users'].byId).length === 0) {
      getInfoAction({ friendlyName: 'users' });
    }

    return () => {
      clearNotifiablesAction(); // Clear notifications at page exit
      deleteInfoAction({ friendlyName });
    };
  }, []);

  const chairInfo = info as ChairInfo;
  const reviewer: ReviewerTableRow | undefined = getReviewer(id, chairInfo.users);

  if (loading) {
    return <Loading />;
  } else if (!reviewer) {
    return <div>Reviewer #{id} not found</div>;
  }

  /* Generate data for table */
  const inputData: unknown[] = [];
  const assignmentData = Object.values(info[friendlyName].byId) as Assignment[];
  assignmentData.forEach((additional) => {
    const newRegister = {
      ...getSubmissionRow(submissionsById[additional.assignable_id], true, keywordById, t),
      assignment_id: additional.id,
      affinity: additional.affinity,
      bid: additional.bid_option_id ? bidOptionsById[additional.bid_option_id].name : undefined,
      conflict: !!additional.conflicts,
      conflicts: additional.conflicts,
      ignored: additional.ignored,
      assigned: additional.assigned,
      completed: additional.id ? additional.completed : undefined,
    };

    inputData.push(newRegister);
  });

  const parentRoute = getRouteByName('RouteAssignmentReviewersToPapers');

  return (
    <div className="flex flex-col h-full">
      <TableHeader
        onBack={() => history.push(fillRoutePath(parentRoute.path))}
        onMove={(rowId) => {
          getInfoAction({ friendlyName, queryParams: { ...queryParams, person_role_id: rowId } });
          history.replace(
            fillRoutePath(getRouteByName('RouteAssignmentReviewersToPapersDetail').path, {
              id: rowId.toString(),
            }),
          );
        }}
        type={'App\\Models\\Person'}
        row={reviewer}
        keywordById={keywordById}
        roleById={roleById}
      />
      <div className="mt-8" />
      <MyGridRedux
        friendlyName={friendlyName}
        inputData={inputData as Record<string, unknown>[]}
        updateAssignmentAction={(modelId, assigned, assignmentId, skipConflicts) => {
          const [dto, keyBy] = buildDTO(modelId, assigned, 'person_role_id', id, 'App\\Models\\Submission');
          updateAssignmentAction({
            ...dto,
            modelId,
            tableFriendlyName: friendlyName,
            queryParams,
            assignmentId,
            skipConflicts,
          });
        }}
        getCustomColumnsData={fetchCustomColumnsData}
      />
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  submissionsById: selectSubmissionState(state).submissionsById,
  loading: selectSubmissionState(state).loading || selectInfo(state).loading,
  keywordById: selectKeywordState(state).keywordById,
  info: state.info.info,
  roleById: selectTrackRoles(state),
  bidOptionsById: selectBidState(state).bidOptions.byId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  deleteInfoAction: (data: InfoDeleteDTO) => dispatch(infoSlice.actions.DELETE(data)),
  clearNotifiablesAction: () => dispatch(tableSlice.actions['NOTIFIABLES:CLEAR']()),
  updateAssignmentAction: (data: UpdateAssignment) => dispatch(assignmentSlice.actions['ASSIGNMENTS:UPDATE'](data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ReviewersToPapersDetailPage);
